import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Debounce shared by the chart's autosaving tabs, so Patient Info and Admission
 * settle at the same pace and their pills run the same countdown. Importing one
 * constant is what keeps them in step; tuning a private copy would not.
 */
export const CHART_AUTOSAVE_DELAY_MS = 800;

export type AutoSaveStatus =
  | 'idle'
  | 'unsaved'
  | 'saving'
  | 'blocked'
  | 'saved'
  | 'error';

/**
 * Throw (or reject) from an onSave handler to signal that the save was
 * refused rather than failed — either the user declined it (e.g. via a
 * confirmation dialog) or the form isn't ready to persist yet (a validation
 * block, where the inline field errors already tell the user why). Autosave
 * producers treat it as "not saved": the value stays dirty (the saved
 * baseline is NOT advanced), the status settles on a quiet 'blocked'
 * ("Not saved"), and there is no error status or console noise.
 */
export class SaveDeclinedError extends Error {
  constructor() {
    super('Save declined by user');
    this.name = 'SaveDeclinedError';
  }
}

export type SaveOutcome = 'saved' | 'declined' | 'error';

interface UseSaveStatusOptions {
  /**
   * How long 'saved' shows before settling back to idle. null (the default)
   * latches the outcome until the next edit, which is the AutoSavePill
   * design; inline indicators that should fade pass a duration.
   */
  savedStatusDuration?: number | null;
  /** Same for 'error'. */
  errorStatusDuration?: number | null;
}

interface UseSaveStatusReturn {
  saveStatus: AutoSaveStatus;
  /** Increments on every edit that resets the debounce timer (drives countdown UI) */
  changeToken: number;
  /** Report an edit that differs from the saved baseline. */
  reportEdit: () => void;
  /** Report an edit that returned the value to the saved baseline. */
  reportRevert: () => void;
  /** Mark a save as started; pass the returned token to resolveSave. */
  beginSave: () => number;
  /**
   * Mark the save identified by token as finished. A 'saved' outcome is
   * dropped when newer edits arrived while the save was in flight, so the
   * status keeps showing those edits as unsaved.
   */
  resolveSave: (token: number, outcome: SaveOutcome) => void;
}

/**
 * Save-status state machine shared by autosave producers: useAutoSave for
 * values the hook owns, and forms like OASISForm whose data lives elsewhere
 * (its zustand store). Owns the status/changeToken pair the AutoSavePill
 * renders, plus the stale-save and decline bookkeeping around it.
 */
export function useSaveStatus({
  savedStatusDuration = null,
  errorStatusDuration = null,
}: UseSaveStatusOptions = {}): UseSaveStatusReturn {
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');
  const [changeToken, setChangeToken] = useState(0);
  const changeSeqRef = useRef(0);
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  const reportEdit = useCallback(() => {
    changeSeqRef.current += 1;
    if (isMountedRef.current) {
      setChangeToken(changeSeqRef.current);
      setSaveStatus('unsaved');
    }
  }, []);

  const reportRevert = useCallback(() => {
    if (isMountedRef.current) {
      // The value is back to the saved baseline, so clear the not-saved
      // resting states ('unsaved' pending and 'blocked' from a refused save)
      // — otherwise a "Not saved" would linger on a form that now matches what
      // is persisted. Leave 'saving' alone: a revert mid-flight must not hide
      // that a save is still running. 'saved'/'error' stay latched as-is.
      setSaveStatus(s => (s === 'unsaved' || s === 'blocked' ? 'idle' : s));
    }
  }, []);

  const beginSave = useCallback(() => {
    if (isMountedRef.current) {
      setSaveStatus('saving');
    }
    return changeSeqRef.current;
  }, []);

  const resolveSave = useCallback(
    (token: number, outcome: SaveOutcome) => {
      if (!isMountedRef.current) return;

      if (outcome === 'declined') {
        // The save was refused (user decline or a validation block), not
        // failed: the value is still unsaved but nothing errored. Surface a
        // quiet 'blocked' ("Not saved") so the indicator never claims success
        // for a save that didn't happen. Guard against a stale decline
        // clobbering a newer edit that already moved us past 'saving'.
        setSaveStatus(s => (s === 'saving' ? 'blocked' : s));
        return;
      }

      // Keep showing "unsaved" if the user edited again while this save
      // was in flight; only a still-current save reports "saved". Errors
      // surface unconditionally.
      if (outcome === 'saved' && changeSeqRef.current !== token) {
        return;
      }

      setSaveStatus(outcome);

      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
      const duration =
        outcome === 'saved' ? savedStatusDuration : errorStatusDuration;
      if (duration != null) {
        statusTimeoutRef.current = setTimeout(() => {
          // Reset to idle unless newer edits already flipped the status
          setSaveStatus(s => (s === outcome ? 'idle' : s));
        }, duration);
      }
    },
    [savedStatusDuration, errorStatusDuration]
  );

  return {
    saveStatus,
    changeToken,
    reportEdit,
    reportRevert,
    beginSave,
    resolveSave,
  };
}

interface UseAutoSaveOptions<T> {
  /** Initial value */
  initialValue: T;
  /** Function to save the value - receives the value to save */
  onSave: (value: T) => Promise<void>;
  /** Debounce delay in milliseconds (default: 1500) */
  debounceDelay?: number;
  /** Time to show "saved" status before returning to idle (default: 2000); null latches it until the next edit */
  savedStatusDuration?: number | null;
  /** Time to show "error" status before returning to idle (default: 3000); null latches it until the next edit */
  errorStatusDuration?: number | null;
  /**
   * Equality used to decide whether the value differs from the last saved
   * baseline (default: Object.is). Right for primitives; pass a structural
   * comparison for object/array values so reverted edits read as clean.
   */
  isEqual?: (a: T, b: T) => boolean;
}

interface UseAutoSaveReturn<T> {
  /** Current local value */
  value: T;
  /** Update local value and trigger debounced save */
  setValue: (newValue: T) => void;
  /** Current save status */
  saveStatus: AutoSaveStatus;
  /**
   * Force an immediate save (e.g. on blur, or to flush before closing a
   * dialog) and resolve to its outcome: 'saved' when the value is persisted
   * or already clean, 'declined' when onSave refused it (validation block or
   * user decline; inline errors already explain why), and 'error' when the
   * save genuinely failed. Callers can hold a dialog open or block a sign on
   * a non-'saved' outcome. Never rejects.
   */
  saveNow: () => Promise<SaveOutcome>;
  /** Check if value has unsaved changes */
  hasUnsavedChanges: boolean;
  /** Increments on every edit that resets the debounce timer (drives countdown UI) */
  changeToken: number;
}

/**
 * Hook for auto-saving values with debouncing.
 *
 * Features:
 * - Debounced auto-save on value change
 * - Immediate save on blur (via saveNow)
 * - Save status tracking (idle, unsaved, saving, saved, error)
 * - Skips save if value hasn't changed from last saved value
 * - Queues pending saves if user types during active save
 *
 * Note: change detection defaults to Object.is, which is correct for
 * primitive values. For objects/arrays, pass an `isEqual` option (or
 * serialized values / stable references).
 *
 * @example
 * ```tsx
 * const { value, setValue, saveStatus, saveNow } = useAutoSave({
 *   initialValue: intake.comments || '',
 *   onSave: async (comments) => {
 *     await updateIntakeRecord(intakeId, { comments });
 *   },
 * });
 *
 * return (
 *   <Textarea
 *     value={value}
 *     onChange={(e) => setValue(e.target.value)}
 *     onBlur={saveNow}
 *   />
 * );
 * ```
 */
export function useAutoSave<T>({
  initialValue,
  onSave,
  debounceDelay = 1500,
  savedStatusDuration = 2000,
  errorStatusDuration = 3000,
  isEqual = Object.is,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn<T> {
  const [localValue, setLocalValue] = useState<T>(initialValue);
  const {
    saveStatus,
    changeToken,
    reportEdit,
    reportRevert,
    beginSave,
    resolveSave,
  } = useSaveStatus({ savedStatusDuration, errorStatusDuration });

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedValueRef = useRef<T>(initialValue);
  const isSavingRef = useRef(false);
  const pendingValueRef = useRef<T | null>(null);
  const currentSavePromiseRef = useRef<Promise<SaveOutcome> | null>(null);
  const isMountedRef = useRef(true);
  // Bumped whenever the save chain drains, to re-run the external-sync effect
  // below: an external value refused while the field was busy is reconsidered
  // as soon as it is idle, rather than being dropped for good.
  const [settleToken, setSettleToken] = useState(0);

  // Use refs for callbacks to avoid stale closure issues
  const onSaveRef = useRef(onSave);
  const isEqualRef = useRef(isEqual);
  useEffect(() => {
    onSaveRef.current = onSave;
    isEqualRef.current = isEqual;
  }, [onSave, isEqual]);

  // Adopt an external value: it becomes both the visible value and the saved
  // baseline. Cancels any armed debounce, because that timer holds the value
  // we are replacing — letting it fire would PUT the discarded local value
  // straight back over the value just adopted (and report it as "Saved").
  const adoptExternalValue = useCallback(
    (value: T) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      setLocalValue(value);
      lastSavedValueRef.current = value;
      // The field now matches what is persisted; drop a lingering "Not saved".
      reportRevert();
    },
    [reportRevert]
  );

  // Sync the local value from `initialValue` (e.g. a server refresh).
  //
  // Contract: an external value may only ever replace a field that is CLEAN
  // and IDLE. It is refused while the value has unsaved edits or a save is in
  // flight, and while it equals the saved baseline (an echo of our own save,
  // which also keeps object values with a custom isEqual from looping on
  // unstable references).
  //
  // A refusal is a deferral, not a drop: this effect re-runs on `localValue`
  // (the field going clean) and on `settleToken` (the save chain draining),
  // and always reconsiders the CURRENT `initialValue`. Re-reading the live
  // prop rather than replaying a value captured at refusal time is what keeps
  // this safe — by the time the field is idle, a snapshot that was stale has
  // normally been superseded by the echo of our own save, and an echo is
  // refused by the baseline check above.
  //
  // EMR-1452: before this guard, a stale server snapshot arriving mid-edit
  // reset the visible text AND rewrote lastSavedValueRef, so the next save
  // persisted the reverted text and the newer typing was genuinely lost.
  useEffect(() => {
    if (isEqualRef.current(initialValue, lastSavedValueRef.current)) {
      return;
    }
    if (isSavingRef.current) {
      return;
    }
    if (!isEqualRef.current(localValue, lastSavedValueRef.current)) {
      return;
    }
    adoptExternalValue(initialValue);
  }, [initialValue, localValue, settleToken, adoptExternalValue]);

  // Cleanup pending debounce on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const performSave = useCallback(
    (valueToSave: T): Promise<SaveOutcome> => {
      // Nothing to persist: the value already matches the saved baseline.
      // Resolve as a successful no-op so a caller flushing a clean form reads
      // 'saved' rather than a false failure.
      if (isEqualRef.current(valueToSave, lastSavedValueRef.current)) {
        return Promise.resolve('saved');
      }

      // A save is already running: queue this value for the in-flight save's
      // finally to pick up. Returning here without assigning
      // currentSavePromiseRef keeps that ref pointing at the genuinely
      // in-flight promise, so saveNow's drain awaits the real work instead of
      // spinning on an already-resolved placeholder. The 'saved' here is a
      // placeholder for an accepted-but-not-yet-run save: only the debounce
      // timer reaches this path and it discards the return, while saveNow
      // drains the chain first so it never sees this value. The queued value's
      // true outcome surfaces once saveNow drains the chain.
      if (isSavingRef.current) {
        pendingValueRef.current = valueToSave;
        return Promise.resolve('saved');
      }

      const promise = (async (): Promise<SaveOutcome> => {
        let outcome: SaveOutcome = 'saved';
        try {
          isSavingRef.current = true;
          pendingValueRef.current = null;
          const saveToken = beginSave();
          try {
            await onSaveRef.current(valueToSave);
            lastSavedValueRef.current = valueToSave;
            resolveSave(saveToken, 'saved');
            outcome = 'saved';
          } catch (error) {
            if (error instanceof SaveDeclinedError) {
              resolveSave(saveToken, 'declined');
              outcome = 'declined';
            } else {
              console.error('Auto-save failed:', error);
              resolveSave(saveToken, 'error');
              outcome = 'error';
            }
          }
        } finally {
          isSavingRef.current = false;
          currentSavePromiseRef.current = null;

          // Check if there's a pending value to save
          if (
            pendingValueRef.current !== null &&
            !isEqualRef.current(
              pendingValueRef.current,
              lastSavedValueRef.current
            )
          ) {
            const pending = pendingValueRef.current;
            pendingValueRef.current = null;
            currentSavePromiseRef.current = performSave(pending);
          } else if (isMountedRef.current) {
            // The chain is drained and the field is idle again. Wake the
            // external-sync effect so an external value refused while this
            // save was running gets reconsidered.
            setSettleToken(token => token + 1);
          }
        }
        return outcome;
      })();
      currentSavePromiseRef.current = promise;
      return promise;
    },
    [beginSave, resolveSave]
  );

  const setValue = useCallback(
    (newValue: T) => {
      setLocalValue(newValue);

      if (!isEqualRef.current(newValue, lastSavedValueRef.current)) {
        reportEdit();
      } else {
        // Edited back to the saved value; nothing pending anymore
        reportRevert();
      }

      // Clear any existing debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new debounce timeout
      debounceTimeoutRef.current = setTimeout(() => {
        performSave(newValue);
      }, debounceDelay);
    },
    [debounceDelay, performSave, reportEdit, reportRevert]
  );

  const saveNow = useCallback(async (): Promise<SaveOutcome> => {
    // Drain the in-flight save plus anything it queued, so the outcome we
    // report reflects the real final state rather than a stale snapshot. The
    // ref only ever holds a genuinely in-flight save (or null), so this
    // settles once the chain is done.
    while (currentSavePromiseRef.current) {
      await currentSavePromiseRef.current;
    }
    // Cancel pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    // Nothing left to flush: the latest value already matches the baseline.
    if (isEqualRef.current(localValue, lastSavedValueRef.current)) {
      return 'saved';
    }
    return performSave(localValue);
  }, [localValue, performSave]);

  const hasUnsavedChanges = !isEqual(localValue, lastSavedValueRef.current);

  return {
    value: localValue,
    setValue,
    saveStatus,
    saveNow,
    hasUnsavedChanges,
    changeToken,
  };
}
